/**
 * Popover — tooltip rico com conteúdo HTML
 * Substitui o title nativo para help contextual
 *
 * Uso:
 *   <Popover content="Texto de ajuda">
 *     <HelpCircle size={14} />
 *   </Popover>
 *
 *   <Popover content={<div>...</div>} placement="top">
 *     <span>hover me</span>
 *   </Popover>
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

export default function Popover({ children, content, placement = 'top', maxWidth = 260 }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos]         = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const show = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    let top, left;
    if (placement === 'top') {
      top  = rect.top  + scrollY - 8;
      left = rect.left + scrollX + rect.width / 2;
    } else if (placement === 'bottom') {
      top  = rect.bottom + scrollY + 8;
      left = rect.left   + scrollX + rect.width / 2;
    } else if (placement === 'right') {
      top  = rect.top  + scrollY + rect.height / 2;
      left = rect.right + scrollX + 8;
    } else {
      top  = rect.top  + scrollY + rect.height / 2;
      left = rect.left + scrollX - 8;
    }
    setPos({ top, left, placement });
    setVisible(true);
  };

  const hide = () => setVisible(false);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!visible) return;
    const handler = (e) => {
      if (!triggerRef.current?.contains(e.target) && !popoverRef.current?.contains(e.target)) {
        hide();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [visible]);

  return (
    <>
      <Trigger
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </Trigger>
      {visible && content && (
        <PopoverBox
          ref={popoverRef}
          style={{ top: pos.top, left: pos.left }}
          $placement={pos.placement}
          $maxWidth={maxWidth}
        >
          {typeof content === 'string' ? <PopoverText>{content}</PopoverText> : content}
          <Arrow $placement={pos.placement} />
        </PopoverBox>
      )}
    </>
  );
}

const Trigger = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: help;
`;

const PopoverBox = styled.div`
  position: absolute;
  z-index: ${theme.zIndex.dropdown + 50};
  max-width: ${p => p.$maxWidth}px;
  background: ${theme.colors.gray900};
  color: ${theme.colors.white};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  box-shadow: ${theme.shadows.lg};
  pointer-events: none;
  font-size: ${theme.typography.xs};
  line-height: 1.5;

  ${p => p.$placement === 'top'    && 'transform: translate(-50%, -100%);'}
  ${p => p.$placement === 'bottom' && 'transform: translate(-50%, 0);'}
  ${p => p.$placement === 'right'  && 'transform: translate(0, -50%);'}
  ${p => p.$placement === 'left'   && 'transform: translate(-100%, -50%);'}
`;

const PopoverText = styled.p`margin: 0; white-space: pre-wrap;`;

const Arrow = styled.div`
  position: absolute;
  width: 0; height: 0;
  border: 5px solid transparent;

  ${p => p.$placement === 'top' && `
    bottom: -10px; left: 50%; transform: translateX(-50%);
    border-top-color: ${theme.colors.gray900};
  `}
  ${p => p.$placement === 'bottom' && `
    top: -10px; left: 50%; transform: translateX(-50%);
    border-bottom-color: ${theme.colors.gray900};
  `}
  ${p => p.$placement === 'right' && `
    left: -10px; top: 50%; transform: translateY(-50%);
    border-right-color: ${theme.colors.gray900};
  `}
  ${p => p.$placement === 'left' && `
    right: -10px; top: 50%; transform: translateY(-50%);
    border-left-color: ${theme.colors.gray900};
  `}
`;
